<template>
  <div>
    <h2 class="mb-5">
      {{ $t('BasicInformation') }}
    </h2>

    <p class="text-grey-dark mb-6">
      {{ $t('BasicInfoSubtitle') }}
    </p>

    <form @submit.prevent>
      <div class="vx-row">

        <div class="vx-col w-full md:w-1/2">
          <vs-input
            v-model.trim="firstName"
            class="w-full required"
            :label="$t('$General.FirstName')"
            :name="$t('$General.FirstName')"
            v-validate="'required|max:100'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-user"
            icon-pack="feather"
            :danger="errors.has($t('$General.FirstName'))"
            :danger-text="errors.first($t('$General.FirstName'))"
            val-icon-danger="clear"/>
        </div>

        <div class="vx-col w-full md:w-1/2 mt-3 md:mt-0">
          <vs-input
            v-model.trim="lastName"
            class="w-full"
            :label="$t('$General.LastName')"
            :name="$t('$General.LastName')"
            v-validate="'required|max:100'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-user"
            icon-pack="feather"
            :danger="errors.has($t('$General.LastName'))"
            :danger-text="errors.first($t('$General.LastName'))"
            val-icon-danger="clear"/>
        </div>
      </div>

      <div class="mt-3">
        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2">
            <vs-input
              v-model.trim="email"
              class="w-full required"
              :label="$tc('$General.Email')"
              :name="$tc('$General.Email')"
              type="email"
              v-validate="'required|email'"
              data-vv-validate-on="blur|input"
              icon-no-border
              icon="icon icon-mail"
              icon-pack="feather"
              :danger="emailHasErrors"
              :danger-text="emailErrorMsg"
              val-icon-danger="clear"
              val-icon-success="check"
              @input="duplicatedEmailError = false"/>
            
            <!-- Unverified status below email field -->
            <div
              v-if="!emailErrorMsg && emailIsNewUnverifiedEmail"
              class="flex items-center mt-0">
              <span class="text-danger">
                {{ $t('NewEmailUnverifiedErrorMsg') }}
              </span>

              <feather-icon
                class="ml-1"
                icon="XIcon"
                svgClasses="h-4 w-4 text-danger"/>
            </div>
            
            <!-- Verified status below email field -->
            <div
              v-if="!emailErrorMsg && !emailIsNewUnverifiedEmail && !emailWasChanged"
              class="flex items-center mt-2">
              <span class="text-success">
                {{ $t('EmailVerifiedMsg') }}
              </span>

              <feather-icon
                class="ml-1"
                icon="CheckCircleIcon"
                svgClasses="h-4 w-4 text-success"/>
            </div>
          </div>

          <div class="vx-col w-full md:w-1/2 mt-3 md:mt-0">
            <vs-input
              v-if="emailIsNewUnverifiedEmail"
              v-model="verificationCode"
              class="w-full"
              :label="$t('VerificationCode')"
              :name="$t('VerificationCode')"
              v-validate="'digits:6'"
              data-vv-validate-on="blur|input"
              icon-no-border
              icon="icon icon-key"
              icon-pack="feather"
              :danger="verifyCodeInputIsInvalid"
              :danger-text="verificationCodeErrorMsg"
              val-icon-danger="clear"
              @input="onVerificationCodeInput"
              placeholder="Enter 6-digit verification code"/>
            
            <!-- Resend verification link - only when unverified -->
            <div v-if="emailIsNewUnverifiedEmail" class="mt-2">
              <a
                href="#"
                class="link-cta cursor-pointer"
                @click.prevent="updateProfileBasicInfo()">
                {{ $t('ResendNewVerificationEmail') }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-start mt-base">
        <vs-button
          class="w-full md:w-auto"
          :disabled="!updateProfileBasicInfoIsValid || !updateProfileBasicInfoShowCancel"
          @click="updateProfileBasicInfo()">
          {{ $t('$General.Update') }}
        </vs-button>

        <vs-button
          v-show="updateProfileBasicInfoShowCancel"
          class="w-full md:w-auto ml-3 vs-button-cancel-flat"
          color="danger"
          @click="resetBasicModel()">
          {{ $t('$General.Cancel') }}
        </vs-button>
      </div>
    </form>

    <vs-popup
      :title="$t('$Auth.$EmailAccountVerification.VerificationModalTitle', {
        email: this.emailToVerify
      })"
      :active.sync="activeModalVerify">
      <transition name="zoom-fade">
        <user-account-profile-basic-verify-email
          v-if="activeModalVerify"
          :email="emailToVerify"
          :verification-token="changeEmailVerificationToken"
          @close="activeModalVerify=false"
          @verified="updatedProfileEmailValidated()"
          @resend="updateProfileBasicInfo(true)">
        </user-account-profile-basic-verify-email>
      </transition>

    </vs-popup>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import UserAccountProfileBasicVerifyEmail from '@/views/modules/user/user-account/user-account-details/UserAccountProfileBasicVerifyEmail.vue';

/**
 * Component to allow the user view and update its profile basic information
 *
 * @module views/modules/user-account/UserAccountProfileBasic.vue
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'UserAccountDetailsBasicInformationBasic',
  i18n: {
    messages: {
      en: {
        BasicInformation: 'User account information',
        BasicInfoSubtitle: 'This section is for your internal account details and system notifications.',
        UpdateBasicInfoNotificationTitle: 'User account information update',
        UpdateBasicInfoNotificationMsg: 'User account information updated successfully',
        DuplicatedEmailErrorMsg: 'That email is taken. Try another one.',
        NewEmailUnverifiedErrorMsg: 'Unverified',
        EmailVerifiedMsg: 'Verified',
        ResendNewVerificationEmail: 'Resend verification',
        VerificationCode: 'Verification code',
        $ResendVerificationEmailNotify: {
          Title: 'Email sent',
          Msg: 'Verification email sent successfully',
        },
      },
    },
  },
  components: {
    UserAccountProfileBasicVerifyEmail,
  },
  data() {
    return {
      activeModalVerify: false,
      emailToVerify: '',
      changeEmailVerificationToken: '',
      firstName: '',
      lastName: '',
      email: '',
      newUnverifiedEmail: '',
      duplicatedEmailError: false,
      verificationCode: '',
      invalidCodeError: '',
    };
  },
  computed: {
    ...mapState({
      authUser: (state) => state.auth.user,
    }),
    emailHasErrors() {
      return this.errors.has(this.$tc('$General.Email'))
        || this.duplicatedEmailError
        || this.emailIsNewUnverifiedEmail;
    },
    emailIsNewUnverifiedEmail() {
      return !!this.newUnverifiedEmail && this.newUnverifiedEmail === this.email;
    },
    emailErrorMsg() {
      let error = this.errors.first(this.$tc('$General.Email'));

      if (this.duplicatedEmailError && !error) {
        error = this.$t('DuplicatedEmailErrorMsg');
      }

      return error || '';
    },
    emailWasChanged() {
      return this.validationFields
        && this.validationFields.Email
        && this.validationFields.Email.changed;
    },
    verifyCodeInputIsInvalid() {
      return this.errors.has(this.$t('VerificationCode')) || !!this.invalidCodeError;
    },
    verificationCodeErrorMsg() {
      return this.errors.first(this.$t('VerificationCode')) || this.invalidCodeError;
    },
    updateProfileBasicInfoIsValid() {
      return !this.errors.any()
        && this.firstName !== '' && this.email !== '';
    },
    updateProfileBasicInfoShowCancel() {
      return Object.keys(this.validationFields).length > 0
        ? Object.keys(this.validationFields).some(
          (key) => this.validationFields[key].changed,
        )
        : false;
    },
  },
  watch: {
    activeModalVerify(val) {
      if (!val) {
        this.changeEmailVerificationToken = '';
        this.$route.query.changeEmailToken = '';
      }
    },
  },
  async created() {
    await this.verifyChangeEmailToken();
    await this.getBasicInformation();
    this.resetBasicModel();
  },
  methods: {
    ...mapActions({
      updateBasicInformation: 'auth/updateBasicInformation',
      getBasicInformation: 'auth/getBasicInformation',
      verifyProfileEmail: 'auth/verifyProfileEmail',
    }),
    async verifyChangeEmailToken() {
      if (this.$route.query.changeEmailToken) {
        const { changeEmailToken } = this.$route.query;

        if (changeEmailToken) {
          this.changeEmailVerificationToken = changeEmailToken;
          this.activeModalVerify = true;
        }

        const uri = window.location.toString();
        if (uri.indexOf('?') > 0) {
          const cleanUri = uri.substring(0, uri.indexOf('?'));
          window.history.replaceState({}, document.title, cleanUri);
        }
      }
    },
    resetBasicModel() {
      this.firstName = this.authUser.firstName || '';
      this.lastName = this.authUser.lastName || '';
      this.email = this.authUser.newUnverifiedEmail || this.authUser.email || '';
      this.newUnverifiedEmail = this.authUser.newUnverifiedEmail || '';
      this.duplicatedEmailError = false;
      this.verificationCode = '';
      this.invalidCodeError = '';

      this.$validator.reset();
    },
    async updateProfileBasicInfo(fromVerifyModal = false) {
      if (this.errors.any()) {
        return;
      }

      this.$vs.loading({ type: 'radius' });

      try {
        await this.updateBasicInformation({
          firstName: this.firstName,
          lastName: this.lastName,
          email: fromVerifyModal ? this.emailToVerify : this.email,
        });

        if (fromVerifyModal) {
          this.$showSuccessActionNotification({
            title: this.$t('$ResendVerificationEmailNotify.Title'),
            text: this.$t('$ResendVerificationEmailNotify.Msg'),
          });
        } else {
          if (this.authUser.email.toLowerCase() !== this.email.toLowerCase()) {
            this.emailToVerify = this.email.toLowerCase();
            this.activeModalVerify = true;
          }

          this.resetBasicModel();

          this.$showSuccessActionNotification({
            title: this.$t('UpdateBasicInfoNotificationTitle'),
            text: this.$t('UpdateBasicInfoNotificationMsg'),
          });
        }
      } catch (e) {
        if (e.response && e.response.data
          && e.response.data.duplicatedEmail) {
          this.duplicatedEmailError = true;
        } else {
          throw e;
        }
      } finally {
        this.$vs.loading.close();
      }
    },
    updatedProfileEmailValidated() {
      this.activeModalVerify = false;
      this.resetBasicModel();
    },
    onVerificationCodeInput() {
      // Clear any previous errors when user types
      this.invalidCodeError = '';
      
      // Auto-verify when 6 digits are entered
      if (this.verificationCode.length === 6 && this.emailIsNewUnverifiedEmail) {
        this.verifyEmailCode();
      }
    },
    async verifyEmailCode() {
      if (this.verificationCode.length !== 6 || !this.emailIsNewUnverifiedEmail) {
        this.invalidCodeError = 'Please enter a valid 6-digit verification code.';
        return;
      }

      this.$vs.loading({ type: 'radius' });

      try {
        await this.verifyProfileEmail({
          email: this.email,
          code: this.verificationCode,
          token: null,
        });

        this.resetBasicModel();

        this.$showSuccessActionNotification({
          title: this.$t('UpdateBasicInfoNotificationTitle'),
          text: this.$t('UpdateBasicInfoNotificationMsg'),
        });
      } catch (e) {
        if (e.response && e.response.data && e.response.data.statusCode === 400) {
          this.invalidCodeError = 'Invalid verification code. Please try again.';
        } else {
          throw e;
        }
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
